/*
 * DROP → Klaviyo email capture.
 * Any form with [data-klaviyo-form] inside an element carrying data-klaviyo-key /
 * data-klaviyo-list subscribes the email to that Klaviyo list through Klaviyo's
 * public client API (no private key involved).
 * Also drives the popup in snippets/email-popup.liquid.
 */
(() => {
  const STORE_KEY = "drop-popup";
  const SESSION_KEY = "drop-popup-session";
  const SNOOZE_DAYS = 14; // after "No thanks" / ×, stay quiet this long
  const EXIT_MIN_MS = 10000; // exit intent only counts after this long on the page
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const read = () => {
    try { return JSON.parse(localStorage.getItem(STORE_KEY) || "{}"); } catch { return {}; }
  };
  const write = (v) => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify({ ...read(), ...v })); } catch {}
  };

  async function subscribe({ key, list, email, source }) {
    const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(key)}`, {
      method: "POST",
      headers: { "Content-Type": "application/vnd.api+json", Accept: "application/vnd.api+json", revision: "2024-10-15" },
      body: JSON.stringify({
        data: {
          type: "subscription",
          attributes: {
            custom_source: source,
            profile: { data: { type: "profile", attributes: { email } } },
          },
          relationships: { list: { data: { type: "list", id: list } } },
        },
      }),
    });
    if (!res.ok) throw new Error(`Klaviyo ${res.status}`);
  }

  function wireForm(form, host, onSuccess) {
    if (form.dataset.wired) return;
    form.dataset.wired = "1";
    const input = form.querySelector('input[type="email"]');
    const error = form.querySelector("[data-klaviyo-error]");
    const button = form.querySelector("[data-klaviyo-submit]");
    const showError = (msg) => {
      if (!error) return;
      error.textContent = msg;
      error.classList.toggle("hidden", !msg);
    };
    input?.addEventListener("input", () => showError(""));
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const email = (input?.value || "").trim();
      if (!EMAIL_RE.test(email)) return showError("Enter a valid email address.");
      button?.setAttribute("disabled", "");
      button?.querySelector("[data-label]")?.classList.add("hidden");
      button?.querySelector("[data-spinner]")?.classList.remove("hidden");
      try {
        await subscribe({ key: host.dataset.klaviyoKey, list: host.dataset.klaviyoList, email, source: host.dataset.source || "DROP website" });
        write({ subscribed: true });
        onSuccess?.();
      } catch (err) {
        console.error(err);
        showError("Something went wrong. Please try again.");
      } finally {
        button?.removeAttribute("disabled");
        button?.querySelector("[data-label]")?.classList.remove("hidden");
        button?.querySelector("[data-spinner]")?.classList.add("hidden");
      }
    });
  }

  function initPopup() {
    const popup = document.querySelector("[data-email-popup]");
    if (!popup) return;
    const tab = document.querySelector("[data-popup-tab]");
    const formView = popup.querySelector("[data-popup-form-view]");
    const successView = popup.querySelector("[data-popup-success-view]");
    const days = (t) => t && Date.now() - t < SNOOZE_DAYS * 864e5;
    const touch = matchMedia("(pointer: coarse)").matches;
    let lastFocus = null;

    // Visitors arriving from a DROP email are already on the list.
    const params = new URLSearchParams(location.search);
    if (params.has("_kx") || /klaviyo/i.test(params.get("utm_source") || "") || params.get("utm_medium") === "email") {
      write({ subscribed: true });
    }

    // Page views this browsing session: the popup only auto-opens once per session.
    let session = {};
    try { session = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}"); } catch {}
    const saveSession = (v) => {
      session = { ...session, ...v };
      try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(session)); } catch {}
    };
    saveSession({ views: (session.views || 0) + 1 });

    const showTab = () => {
      const st = read();
      if (!tab || st.subscribed || days(st.tabHiddenAt)) return;
      if (tab.hasAttribute("data-desktop-only") && !matchMedia("(min-width: 768px)").matches) return;
      tab.classList.remove("hidden");
      tab.classList.add("flex");
    };
    const hideTab = () => {
      tab?.classList.add("hidden");
      tab?.classList.remove("flex");
    };

    const open = () => {
      if (!popup.classList.contains("hidden")) return;
      lastFocus = document.activeElement;
      popup.classList.remove("hidden");
      popup.classList.add("is-open");
      hideTab();
      document.body.style.overflow = "hidden";
      // No autofocus on phones: it would throw the keyboard up before they chose to type.
      if (!touch) setTimeout(() => popup.querySelector('input[type="email"]')?.focus({ preventScroll: true }), 350);
    };
    const close = () => {
      if (popup.classList.contains("hidden")) return;
      popup.classList.add("hidden");
      popup.classList.remove("is-open");
      document.body.style.overflow = "";
      if (!read().subscribed) {
        write({ dismissedAt: Date.now() });
        showTab();
      }
      lastFocus?.focus?.({ preventScroll: true });
    };

    popup.addEventListener("click", (e) => { if (e.target.closest("[data-popup-close]")) close(); });
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") close(); });
    tab?.querySelector("[data-popup-open]")?.addEventListener("click", open);
    tab?.querySelector("[data-popup-tab-hide]")?.addEventListener("click", () => {
      write({ tabHiddenAt: Date.now() });
      hideTab();
    });

    wireForm(popup.querySelector("[data-klaviyo-form]"), popup, () => {
      formView.classList.add("hidden");
      successView.classList.remove("hidden");
    });

    const state = read();
    if (state.subscribed) return;
    if (days(state.dismissedAt)) return showTab();
    if (session.shown || (window.Shopify && window.Shopify.designMode)) return;

    // Never interrupt someone who is shopping, typing, or has a drawer/menu open.
    const busy = () =>
      document.body.style.overflow === "hidden" ||
      document.documentElement.classList.contains("cart-is-open") ||
      Array.from(document.querySelectorAll("[data-cart-count]")).some((el) => Number(el.textContent) > 0) ||
      /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || "");

    const start = Date.now();
    let engaged = session.views > 1; // second page, or a real scroll on the first one
    let due = false;
    let done = false;

    const tryOpen = () => {
      if (done || !due || !engaged || document.hidden) return;
      if (busy()) return; // try again on the next scroll / timer tick
      done = true;
      saveSession({ shown: true });
      cleanup();
      open();
    };
    const onScroll = () => {
      if (!engaged && scrollY > innerHeight * 0.6) engaged = true;
      tryOpen();
    };
    // Desktop only: the cursor leaving through the top of the window, after a real visit.
    const onLeave = (e) => {
      if (e.relatedTarget || e.clientY > 0 || Date.now() - start < EXIT_MIN_MS) return;
      due = engaged = true;
      tryOpen();
    };
    const timer = setTimeout(() => { due = true; tryOpen(); }, Number(popup.dataset.delay) || 15000);
    const retry = setInterval(tryOpen, 5000);
    const cleanup = () => {
      clearTimeout(timer);
      clearInterval(retry);
      removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseout", onLeave);
    };
    addEventListener("scroll", onScroll, { passive: true });
    if (!touch) document.addEventListener("mouseout", onLeave);
  }

  // Standalone forms (e.g. the password page) that also subscribe to Klaviyo.
  function initInlineForms() {
    document.querySelectorAll("[data-klaviyo-inline]").forEach((host) => {
      const form = host.querySelector("[data-klaviyo-form]");
      if (!form) return;
      wireForm(form, host, () => {
        host.querySelector("[data-inline-form-view]")?.classList.add("hidden");
        host.querySelector("[data-inline-success]")?.classList.remove("hidden");
      });
    });
  }

  const init = () => { initPopup(); initInlineForms(); };
  document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", init) : init();
})();
